import React, { useEffect } from 'react';
import Script from 'react-load-script';
import { SequenceAnimator } from 'react-sequence-animator';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { Router } from '@gatsbyjs/reach-router';
import LogoSequence from 'lodash';
// eslint-disable-next-line import/order
import { Error } from '@material-ui/icons';
import '@shcc/styles/main.scss';
import '@assets/styles/global.scss';

import TagManager from 'react-gtm-module';

import styles from './app.module.scss';
import { Modal as GlobalModal } from '@common/components';
import { AnimateWhileLoading, Layout } from '@components';
import i18n from '@components/i18n/i18n';
import {
  appDirection,
  appLanguage,
  appLogin,
  appLogout,
  appPrefixLanguage,
  gigyaLoaded,
  hideModal,
  toggleLoading,
} from '@store/appStateSlice';
import { removeAccessToken, retrieveAccessToken, validateToken } from '@utils';
import { fetchProfile, profileReset } from '@store/userProfileSlice';
import { getLanguagePrefix } from '@helpers';
import PublicRoute from '@components/PublicRoute';
import { GIGYA_ENDPOINT, GIGYA_PUBLIC_KEY } from '@constants';
import { FullPageLayout } from '@components/FullPageLayout/FullPageLayout';
import { documentsReset } from '@store/documentsSlice';
import PrivateRoute from '@components/PrivateRoute';
import Logout from '@views/Logout';
import Login from '@views/Login';
import PortalHeader from '@components/PortalHeader';
import withI18next from '@components/i18n/withI18next';
import { NotFoundPage } from '@views';
import BigCommerceSSO from '@src/views/BigCommerce/BigCommerceSSO';
import { Wallet } from '@views/Wallet';
import { SupportedPayments } from '@views/SupportedPayments';
import { LeadCollectionTemplate } from '@views/SelfService/LeadCollector';

const handleLogout = dispatch => {
  gigya.accounts.logout({
    callback: () => {
      removeAccessToken();
      dispatch(appLogout());
      dispatch(profileReset());
      dispatch(documentsReset());
    },
  });
};

// eslint-disable-next-line react/display-name
const withAppComponent = Component => props =>
  <Component location={location} {...props} />;

const App = ({ location }) => {
  const tagManagerArgs = {
    gtmId: process.env.REACT_APP_GTM_ID,
  };

  TagManager.initialize(tagManagerArgs);

  const dispatch = useDispatch();
  const {
    appState: { isLoggedIn, showModal },
  } = useSelector(state => state);
  const prefixLanguage = i18n.language;

  const language = 'en'; // i18n.language; // Change this to 'en' to force all Portal content to English only, independant of path prefix
  const direction = 'ltr'; // i18n.dir(); // Change to 'ltr' if forcing the language to English above
  const basepath = `${getLanguagePrefix(prefixLanguage)}/commerce/`;
  useEffect(() => {
    dispatch(appPrefixLanguage(prefixLanguage));
    dispatch(appLanguage(language));
    dispatch(appDirection(direction));
  });

  useEffect(() => {
    if (showModal) {
      dispatch(hideModal());
    }
  }, [location.pathname]);

  const token = retrieveAccessToken();
  const validToken = validateToken(token);
  global = {
    __PATH_PREFIX__: ``,
    __BASE_PATH__: ``, // add this line
  };

  window.__BASE_PATH__ = '/';
  window.___loader = {
    enqueue: () => {},
    hovering: () => {},
  };

  useEffect(() => {
    if (validToken) {
      dispatch(toggleLoading(false));
      dispatch(appLogin(token));
      dispatch(fetchProfile());
    } else if (isLoggedIn && !validToken) {
      handleLogout(dispatch);
      dispatch(toggleLoading(false));
    } else {
      dispatch(toggleLoading(false));
    }
  }, [validToken, isLoggedIn]);

  if (
    location.pathname.indexOf('/commerce/orders') >= 0 ||
    location.pathname.indexOf('/commerce/interested') >= 0
  ) {
    return <FullPageLayout location={location} basepath={basepath} />;
  }
  return <DefaultLayout location={location} />;
};

const DefaultLayout = ({ location }) => {
  const dispatch = useDispatch();
  const {
    appState: { isLoggedIn, isLoading },
    userProfile: { isProfileLoading, isProfileError },
  } = useSelector(state => state);
  const prefixLanguage = i18n.language;
  const direction = 'ltr'; // i18n.dir(); // Change to 'ltr' if forcing the language to English above
  const basepath = `${getLanguagePrefix(prefixLanguage)}/commerce/`;
  const isPortalLoading = isProfileLoading || isLoading;
  const sequenceChildren = LogoSequence.map((imageURL, index) => (
    <img
      className={styles.loader}
      src={imageURL}
      key={index}
      alt={`animation sequence frame ${index}`}
    />
  ));
  const showPortalHeader =
    !isPortalLoading &&
    (!location.pathname.includes('/portal/confirmation') ||
      !location.pathname.includes('/commerce/'));

  const handleGigyaError = error => {
    // eslint-disable-next-line no-console
    console.error('Gigya Failed to Load', error);
  };

  const handleGigyaLoaded = () => {
    dispatch(gigyaLoaded());
  };
  const isWalletEnabled = new URL(window.location.href).searchParams.get(
    'showWallet'
  );
  const isPaymentEnabled = new URL(window.location.href).searchParams.get(
    'showPayment'
  );

  return (
    <Layout
      theme="dark"
      type="portal"
      location={location}
      direction={direction}
      backgroundColor={'#E7E7E4'}
      loggedIn={isLoggedIn}
    >
      {showPortalHeader && (
        <PortalHeader location={location} isLoggedIn={isLoggedIn} />
      )}
      <AnimateWhileLoading>
        {isProfileError && <Error />}
        {isPortalLoading && (
          <motion.div
            className={styles.appPageLoader}
            initial={{ width: '100%', opacity: 0 }}
            animate={{ width: '100%', opacity: 1 }}
            exit={{ width: '100%', opacity: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className={styles.loaderWrapper}>
              {/* eslint-disable-next-line react/no-children-prop */}
              <SequenceAnimator children={sequenceChildren} duration={3500} />
            </div>
          </motion.div>
        )}
        {!isPortalLoading && !isProfileError && (
          <Router primary={false} basepath={basepath}>
            <PrivateRoute path="/logout/" component={Logout} />
            {isPaymentEnabled && (
              <PublicRoute
                path="/supported/payments"
                component={SupportedPayments}
              />
            )}
            <PublicRoute path="/login/" component={Login} />
            <PublicRoute path="/" component={Login} />
            <PublicRoute default component={NotFoundPage} />
            <PublicRoute
              path="/authenticate/bigcommerce"
              component={BigCommerceSSO}
            />
            {isWalletEnabled && (
              <PrivateRoute path="/account/wallet" component={Wallet} />
            )}
          </Router>
        )}
      </AnimateWhileLoading>
      <GlobalModal />
      <Script
        url={`${GIGYA_ENDPOINT}?apikey=${GIGYA_PUBLIC_KEY}`}
        onError={handleGigyaError}
        onLoad={handleGigyaLoaded}
      />
    </Layout>
  );
};

export default withI18next(withAppComponent(App));
